import React, { useCallback, useMemo } from 'react'

import { useAuthInfo } from '@sweetauth/core'

import { SubscriberPicker } from '@/components/common/SubscriberPicker'

import { useOrganizationUsers } from '@/hooks/organization/useOrganizationUsers'

import type { UUID } from '@/types/Id'
import type { User } from '@/types/User'

// Stable empty fallback so the picker doesn't get a fresh [] each render.
const EMPTY_USERS: User[] = []

interface MembersStepProps {
  orgId: UUID | ''
  selectedUserIds: string[]
  onChange: (userIds: string[]) => void
}

export const MembersStep: React.FC<MembersStepProps> = ({
  orgId,
  selectedUserIds,
  onChange,
}) => {
  const { user: currentUser } = useAuthInfo()
  const { data: users, isLoading } = useOrganizationUsers({ orgId })
  const currentUserId = (currentUser?.userId as UUID | undefined) ?? null

  // The creator is always included at submit time, so their row is locked:
  // pre-selected, greyed out, and not deselectable.
  const lockedIds = useMemo(
    () => (currentUserId ? [currentUserId] : []),
    [currentUserId],
  )

  const handleToggle = useCallback(
    (userId: UUID) => {
      onChange(
        selectedUserIds.includes(userId)
          ? selectedUserIds.filter((id) => id !== userId)
          : [...selectedUserIds, userId],
      )
    },
    [onChange, selectedUserIds],
  )

  const handleToggleAll = useCallback(
    (visibleIds: UUID[]) => {
      const selected = new Set(selectedUserIds)
      const allVisibleSelected = visibleIds.every((id) => selected.has(id))
      if (allVisibleSelected) {
        const visible = new Set<string>(visibleIds)
        onChange(selectedUserIds.filter((id) => !visible.has(id)))
      } else {
        for (const id of visibleIds) {
          selected.add(id)
        }
        onChange([...selected])
      }
    },
    [onChange, selectedUserIds],
  )

  return (
    <SubscriberPicker
      users={users ?? EMPTY_USERS}
      // rows only render once users load, which requires a real org id
      orgId={orgId as UUID}
      isLoading={isLoading}
      selectedIds={selectedUserIds as UUID[]}
      currentUserId={currentUserId}
      onToggle={handleToggle}
      onToggleAll={handleToggleAll}
      fillHeight={true}
      searchName="pursuit-member-search"
      emptyMessage="No other teammates in this organization yet. You can invite people later from the pursuit's access settings."
      noMatchesMessage="No teammates match your search."
      lockedIds={lockedIds}
      lockedHint="Always included"
    />
  )
}

MembersStep.displayName = 'CreatePursuitDialog.MembersStep'
